import React, { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { BarChart3, ClipboardCheck, Cog, Compass, Layers, ListChecks, LogOut, Users } from "lucide-react";
import { api } from "../api/client";
import type { DemoStatus } from "../api/types";
import { useSession } from "../store/session";
import { cx } from "./Controls";
import { BrandMark } from "./BrandMark";
import { GuidedTour } from "./GuidedTour";

export const DEMO_EVENT = "insp:demo-changed";

const ADMIN_NAV = [
  { to: "/piezas", label: "Piezas", icon: Layers, id: "parts" },
  { to: "/inspecciones", label: "Inspecciones", icon: ListChecks, id: "inspections" },
  { to: "/operarios", label: "Operarios", icon: Users, id: "operators" },
  { to: "/indicadores", label: "Indicadores", icon: BarChart3, id: "metrics" },
  { to: "/registrar", label: "Registrar", icon: ClipboardCheck, id: "register" },
  { to: "/ajustes", label: "Ajustes", icon: Cog, id: "settings" },
];
const OPERATOR_NAV = [
  { to: "/registrar", label: "Registrar", icon: ClipboardCheck, id: "register" },
  { to: "/inspecciones", label: "Mis inspecciones", icon: ListChecks, id: "inspections" },
];

function DemoBanner() {
  const [active, setActive] = useState(false);
  const load = useCallback(() => {
    api.get<DemoStatus>("/demo/status").then((s) => setActive(s.active)).catch(() => undefined);
  }, []);
  useEffect(() => {
    load();
    window.addEventListener(DEMO_EVENT, load);
    return () => window.removeEventListener(DEMO_EVENT, load);
  }, [load]);
  if (!active) return null;
  return (
    <div className="bg-mark px-4 py-1.5 text-center text-xs font-semibold text-ink sm:text-sm" data-testid="demo-banner">
      <span className="sm:hidden">MODO DEMO · Datos de ejemplo, no reales.</span>
      <span className="hidden sm:inline">MODO DEMO · Operarios, piezas, fotos e inspecciones de ejemplo. No son datos reales y la IA no está entrenada con ellos.</span>
    </div>
  );
}

export default function Layout() {
  const { session, logout } = useSession();
  const navigate = useNavigate();
  const op = session.operator;
  const nav = op.role === "admin" ? ADMIN_NAV : OPERATOR_NAV;
  const [tour, setTour] = useState(false);
  const doLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };
  return (
    <div className="min-h-screen bg-paper">
      <aside className="on-dark fixed inset-y-0 left-0 z-30 hidden w-60 flex-col bg-graphite text-slate-300 lg:flex">
        <div className="flex items-center gap-3 px-5 py-6">
          <BrandMark className="h-8 w-8" />
          <div>
            <div className="font-display text-lg font-bold leading-none text-white">Ally Quality</div>
            <div className="mt-1 text-xs text-slate-400">Inspección visual</div>
          </div>
        </div>
        <nav className="flex-1 space-y-1 px-3">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} data-testid={`nav-${n.id}`} className={({ isActive }) => cx("relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150", isActive ? "bg-graphite-2 text-white before:absolute before:inset-y-2 before:left-0 before:w-[3px] before:rounded-full before:bg-mark" : "hover:bg-graphite-2/60 hover:text-white")}>
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
      </aside>
      <div className="lg:pl-60">
        <div className="sticky top-0 z-20">
        <DemoBanner />
        <header className="flex h-14 items-center justify-between border-b border-line bg-paper/85 px-4 backdrop-blur-md sm:px-8">
          <span className="flex items-center gap-2 lg:hidden"><BrandMark tone="light" className="h-6 w-6" /><span className="font-display font-bold text-ink">Ally Quality</span></span>
          <div className="ml-auto flex items-center gap-3 text-sm">
            <button onClick={() => setTour(true)} className="inline-flex items-center gap-1.5 rounded-lg border border-line bg-white px-2.5 py-1.5 font-medium text-ink hover:bg-slate-50" data-testid="tour-start-button">
              <Compass className="h-4 w-4" /> Guía
            </button>
            <span className="hidden text-slate-600 sm:inline" data-testid="header-user-name">{op.name}</span>
            <span className="rounded-md bg-slate-200/70 px-1.5 py-0.5 text-xs text-slate-600">{op.role === "admin" ? "Admin" : "Operario"}</span>
            <button onClick={doLogout} className="inline-flex items-center gap-1.5 rounded-md px-2 py-1.5 text-slate-600 hover:bg-slate-100 hover:text-ink" data-testid="logout-button">
              <LogOut className="h-4 w-4" /> Salir
            </button>
          </div>
        </header>
        </div>
        <main className="mx-auto max-w-7xl px-4 pb-28 pt-6 sm:px-8 lg:pb-12">
          <Outlet />
        </main>
      </div>
      <nav className="fixed inset-x-0 bottom-0 z-30 flex border-t border-line bg-white lg:hidden" data-testid="bottom-nav">
        {nav.map((n) => (
          <NavLink key={n.to} to={n.to} data-testid={`bottom-nav-${n.id}`} className={({ isActive }) => cx("relative flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] font-medium", isActive ? "text-ink before:absolute before:inset-x-6 before:top-0 before:h-[3px] before:rounded-b-full before:bg-mark" : "text-slate-500")}>
            <n.icon className="h-5 w-5" />
            <span className="truncate">{n.label}</span>
          </NavLink>
        ))}
      </nav>
      <GuidedTour open={tour} role={op.role} onClose={() => setTour(false)} />
    </div>
  );
}
